// cspell:disable

import React, { useEffect, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import toast from 'react-hot-toast';
import blogFetch from '../../axios/config';
import QRCodeReaderOrdem from './QRCodeReaderOrdem';
import { ModalQRInspFinalProps, VerificacaoQRInspFinal } from './Types';

const ROTA_VERIFICAR_QR = 'Quality/verificar-qr';
const DOMINIO_ESPERADO = 'q.agpbrasil.com.br';
const TOAST = { duration: 5000 };

const ultimoSegmento = (valor: string): string =>
    valor.trim().replace(/\/+$/, '').split('/').filter(Boolean).pop() || '';

const pareceLink = (valor: string): boolean => {
    const v = (valor || '').toLowerCase();
    return v.startsWith('http') || v.includes(DOMINIO_ESPERADO);
};

const lerVerificacao = (data: any, ordemLida: string, url: string): VerificacaoQRInspFinal => ({
    aprovado: Boolean(data?.aprovado),
    resultado: String(data?.resultado ?? ''),
    mensagem: data?.mensagem ?? data?.message ?? '',
    ordem: String(data?.ordem ?? ordemLida),
    loteEsperado: String(data?.loteEsperado ?? ''),
    loteLido: String(data?.loteLido ?? ultimoSegmento(url)),
    urlLida: String(data?.urlLida ?? url),
    setorAtual: String(data?.setorAtual ?? ''),
    apontado16ACV: Boolean(data?.apontado16ACV),
    no18INSFIN: Boolean(data?.no18INSFIN),
    apontamentoLiberado: Boolean(data?.apontamentoLiberado),
    verificacaoKey: data?.verificacaoKey ?? null,
});

const isTeclaConfirma = (e: React.KeyboardEvent<HTMLInputElement>): boolean =>
    e.key === 'Enter' || e.key === 'NumpadEnter' || e.key === 'Tab';

const AVISO_NAO_AVANCAR = 'NÃO PODE AVANÇAR COM A PEÇA';

// QR não confere com a ordem lida
const motivoQRErrado = (r: VerificacaoQRInspFinal): string => {
    if (r.resultado === 'ERRO') return 'Não foi possível verificar o QR no servidor.';
    if (r.loteEsperado && r.loteLido && r.loteEsperado !== r.loteLido)
        return `O QR lido é do lote ${r.loteLido} e a ordem informada é do lote ${r.loteEsperado}.`;
    return 'O QR lido não corresponde à ordem informada.';
};

// QR confere, mas o fluxo não permite apontar
const motivoBloqueio = (r: VerificacaoQRInspFinal): string => {
    if (!r.apontado16ACV) return 'A peça não foi apontada no 16ACV (Autoclave).';
    if (!r.no18INSFIN)
        return `A peça não está no 18INSFIN${r.setorAtual ? ` — o setor atual é ${r.setorAtual}` : ''}.`;
    return 'Apontamento bloqueado para esta ordem.';
};

const ModalQRInspFinal: React.FC<ModalQRInspFinalProps> = ({
    show,
    onHide,
    matricula,
    matriculaSap,
    operador,
    onTrocarOperador,
    processando,
    onAprovado,
}) => {

    const [ordemInput, setOrdemInput] = useState<string>('');
    const [qrInput, setQrInput] = useState<string>('');
    const [verificando, setVerificando] = useState<boolean>(false);
    const [resultado, setResultado] = useState<VerificacaoQRInspFinal | null>(null);
    const [apontado, setApontado] = useState<boolean>(false);

    const limparLeitura = () => {
        setOrdemInput('');
        setQrInput('');
        setResultado(null);
        setApontado(false);
    };

    useEffect(() => {
        if (!show) return;
        limparLeitura();
        setVerificando(false);
    }, [show]);

    const relerQR = () => {
        setQrInput('');
        setResultado(null);
        setApontado(false);
    };

    const fechar = () => {
        limparLeitura();
        setVerificando(false);
        onHide();
    };

    const concluirApontamento = async (ordemAprovada: string) => {
        const apontou = await onAprovado?.(ordemAprovada);
        if (!apontou) return;
        setApontado(true);
        setTimeout(limparLeitura, 1500);
    };

    const verificar = async (valorLido: string) => {
        const url = (valorLido ?? '').trim();
        const ordemAlvo = ordemInput.trim();

        if (!ordemAlvo) {
            toast.error('Leia ou digite a ordem da ficha antes do QR da peça.', TOAST);
            return;
        }
        if (!url) return;

        setQrInput(url);
        setResultado(null);
        setApontado(false);
        setVerificando(true);
        try {
            const { data } = await blogFetch.get(ROTA_VERIFICAR_QR, {
                params: { ordem: ordemAlvo, url, matricula: matriculaSap ?? '' },
                timeout: 8000,
            });

            const veredito = lerVerificacao(data, ordemAlvo, url);
            setResultado(veredito);

            if (!veredito.aprovado) {
                toast.error(`QR ERRADO — ${AVISO_NAO_AVANCAR}. ${motivoQRErrado(veredito)}`, TOAST);
                return;
            }
            if (!veredito.apontamentoLiberado) {
                toast.error(`${AVISO_NAO_AVANCAR}. ${motivoBloqueio(veredito)}`, TOAST);
                return;
            }
            await concluirApontamento(veredito.ordem || ordemAlvo);
        } catch (error: any) {
            console.error('Erro ao verificar o QR:', error);
            const veredito = lerVerificacao(error?.response?.data, ordemAlvo, url);
            setResultado({
                ...veredito,
                mensagem: veredito.mensagem || 'Erro de comunicação com o servidor.',
                resultado: veredito.resultado || 'ERRO',
            });
            toast.error(
                `${AVISO_NAO_AVANCAR}. ${veredito.mensagem || 'Não foi possível verificar o QR no servidor — leia novamente.'}`,
                TOAST
            );
        } finally {
            setVerificando(false);
        }
    };

    const handleLeitura = (valorBruto: string) => {
        const valor = (valorBruto || '').trim();
        if (!valor) return;
        if (pareceLink(valor)) {
            verificar(valor);
            return;
        }
        setOrdemInput(valor);
        setQrInput('');
        setResultado(null);
        setApontado(false);
    };

    const ordemPronta = !!ordemInput.trim();
    const ocupado = verificando || !!processando;
    const naoPodeAvancar = !!resultado && !apontado && (!resultado.aprovado || !resultado.apontamentoLiberado);

    return (
        <Modal show={show} onHide={fechar} centered backdrop="static" size="lg">
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="fw-bold fs-4">Apontamento Inspeção Final</Modal.Title>
            </Modal.Header>

            <Modal.Body className="py-4">
                {(operador || onTrocarOperador) && (
                    <div className="d-flex align-items-center justify-content-between mb-3 p-2 bg-light border rounded">
                        <span>
                            <span className="text-muted">Operador: </span>
                            {operador
                                ? <span className="fw-bold">{operador}{matricula ? ` (${matricula})` : ''}</span>
                                : <span className="text-muted fst-italic">não identificado</span>}
                        </span>
                        {onTrocarOperador && (
                            <Button
                                variant={operador ? "outline-secondary" : "warning"}
                                size="sm"
                                onClick={onTrocarOperador}
                                disabled={ocupado}
                            >
                                {operador ? "Trocar" : "Registrar"}
                            </Button>
                        )}
                    </div>
                )}

                {ocupado && (
                    <div className="text-center my-3 fs-5">
                        <Spinner animation="border" className="me-2" />
                        {verificando ? "Verificando..." : "Apontando..."}
                    </div>
                )}

                <div className="mb-4">
                    <h6 className="fw-bold text-secondary mb-2">1 · Ficha (ordem)</h6>
                    <input
                        type="text"
                        className="form-control form-control-lg"
                        placeholder="Leia ou digite a ordem da ficha"
                        value={ordemInput}
                        autoFocus
                        autoComplete="off"
                        disabled={ocupado}
                        onChange={(e) => { setOrdemInput(e.target.value); setResultado(null); setApontado(false); }}
                        onKeyDown={(e) => { if (isTeclaConfirma(e)) e.preventDefault(); }}
                    />
                    <QRCodeReaderOrdem onLeitura={handleLeitura} />
                </div>

                <div className="mb-2">
                    <h6 className="fw-bold text-secondary mb-2">2 · QR da peça</h6>
                    {!ordemPronta ? (
                        <p className="text-muted mb-0">Informe a ordem da ficha para liberar a leitura da peça.</p>
                    ) : !resultado ? (
                        <>
                            <input
                                type="text"
                                className="form-control form-control-lg"
                                placeholder="Leia ou cole o QR da peça"
                                value={qrInput}
                                autoComplete="off"
                                disabled={ocupado}
                                onChange={(e) => setQrInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (!isTeclaConfirma(e)) return;
                                    e.preventDefault();
                                    verificar(qrInput);
                                }}
                            />
                            <QRCodeReaderOrdem onLeitura={handleLeitura} />
                        </>
                    ) : (
                        <div
                            className={`border rounded p-3 ${apontado
                                ? 'border-success bg-success bg-opacity-10'
                                : naoPodeAvancar
                                    ? 'border-danger bg-danger bg-opacity-10'
                                    : 'border-warning bg-warning bg-opacity-10'}`}
                        >
                            <div
                                className={`fw-bold text-center mb-3 ${resultado.aprovado ? 'text-success' : 'text-danger'}`}
                                style={{ fontSize: '2rem' }}
                            >
                                {apontado
                                    ? '✓ PEÇA APONTADA'
                                    : resultado.aprovado ? '✓ QR APROVADO' : '✗ QR ERRADO'}
                            </div>

                            {naoPodeAvancar && (
                                <div className="bg-danger text-white rounded p-3 mb-3 text-center">
                                    <div className="fw-bold" style={{ fontSize: '1.6rem', lineHeight: 1.2 }}>
                                        ⛔ {AVISO_NAO_AVANCAR}
                                    </div>
                                    <div className="fw-semibold fs-5 mt-2">
                                        {resultado.aprovado ? motivoBloqueio(resultado) : motivoQRErrado(resultado)}
                                    </div>
                                    <div className="mt-2">
                                        Separe a peça e avise a liderança antes de seguir com ela.
                                    </div>
                                </div>
                            )}

                            {resultado.mensagem && !apontado && (
                                <div className={`text-center mb-3 ${naoPodeAvancar ? 'text-muted' : 'fs-5'}`}>
                                    {resultado.mensagem}
                                </div>
                            )}

                            <ul className="list-unstyled mb-0 fs-5">
                                <li className="d-flex justify-content-between border-top py-2">
                                    <span className="text-muted">Ordem</span>
                                    <span className="fw-bold text-end">{resultado.ordem || '—'}</span>
                                </li>
                                {resultado.loteEsperado && (
                                    <li className="d-flex justify-content-between border-top py-2">
                                        <span className="text-muted">Lote esperado</span>
                                        <span className="fw-bold text-success text-end">{resultado.loteEsperado}</span>
                                    </li>
                                )}
                                <li className="d-flex justify-content-between border-top py-2">
                                    <span className="text-muted">Lote lido</span>
                                    <span className={`fw-bold text-end ${resultado.aprovado ? 'text-success' : 'text-danger'}`}>
                                        {resultado.loteLido || '—'}
                                    </span>
                                </li>
                                {resultado.setorAtual && (
                                    <li className="d-flex justify-content-between border-top py-2">
                                        <span className="text-muted">Setor atual</span>
                                        <span className={`fw-bold text-end ${resultado.no18INSFIN ? 'text-success' : 'text-danger'}`}>
                                            {resultado.setorAtual}
                                        </span>
                                    </li>
                                )}
                                <li className="d-flex justify-content-between border-top py-2">
                                    <span className="text-muted">16ACV</span>
                                    <span className={`fw-bold text-end ${resultado.apontado16ACV ? 'text-success' : 'text-danger'}`}>
                                        {resultado.apontado16ACV ? 'apontado' : 'não apontado'}
                                    </span>
                                </li>
                            </ul>

                            {!apontado && resultado.apontamentoLiberado && (
                                <Button
                                    variant="success"
                                    size="lg"
                                    className="w-100 mt-3 fw-semibold"
                                    onClick={() => concluirApontamento(resultado.ordem)}
                                    disabled={ocupado}
                                >
                                    ✓ Apontar
                                </Button>
                            )}

                            {!apontado && (
                                <Button
                                    variant="outline-secondary"
                                    size="lg"
                                    className="w-100 mt-3"
                                    onClick={relerQR}
                                    disabled={ocupado}
                                >
                                    Ler outro QR
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={fechar} disabled={ocupado}>
                    Fechar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalQRInspFinal;
